"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { useSession } from "@/contexts/SessionContext";

import { useUsernameCheck } from "@/hooks/useUsernameCheck";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Loader2 from "lucide-react/dist/esm/icons/loader";
import Mail from "lucide-react/dist/esm/icons/mail";
import ArrowRight from "lucide-react/dist/esm/icons/arrow-right";
import AtSign from "lucide-react/dist/esm/icons/at-sign";
import Check from "lucide-react/dist/esm/icons/check";
import X from "lucide-react/dist/esm/icons/x";
import { Checkbox } from "@/components/ui/checkbox";
import SocialAuthButtons from "@/components/auth/SocialAuthButtons";
import AuthLayout from "@/components/auth/AuthLayout";
import PasswordInput from "@/components/auth/PasswordInput";
import PasswordStrength from "@/components/auth/PasswordStrength";
import { trackSignUp } from "@/lib/analytics";
import { trackAuthSignupCompleted, trackAuthSignupStarted } from "@/lib/analytics/auth-tracking";
import {
  AuthFormPanel,
  AuthSkipLink,
  AuthFieldShell,
} from "@/components/auth/auth-form-ui";
import { FormAnticipationBar } from "@/components/ui/loading-anticipation";
import { Skeleton } from "@/components/ui/skeleton";
import { reportErrorToDiscord, toUserFriendlyAuthError } from "@/lib/error-reporting";
import { ResendVerification } from "@/components/auth/ResendVerification";
import { CheckYourEmailScreen } from "@/components/auth/CheckYourEmailScreen";
import { DiscordBanner } from "@/components/ui/discord-cta";
import { evaluatePasswordPolicy } from "@/lib/password-policy";
import { cn } from "@/lib/utils";
import { useCaptureReferral } from "@/hooks/useCaptureReferral";
import { stashPendingConsent } from "@/lib/pending-consent";
import { peekPendingIntent, savePendingIntent } from "@/lib/pending-intent";
import { safeRedirectPath } from "@/lib/safe-redirect";
import { TurnstileWidget, isTurnstileEnabled } from "@/components/auth/TurnstileWidget";
import Gift from "lucide-react/dist/esm/icons/gift";
import { HIGHEST_SELLER_SHARE_PERCENT } from "@/lib/platform-fees";
import { track } from "@/lib/analytics";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SignUp() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [wantsSeller, setWantsSeller] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    email?: string;
    password?: string;
    username?: string;
  }>({});
  const [success, setSuccess] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [redirectTo, setRedirectTo] = useState<string | null>(null);
  const { signUp } = useSession();
  const router = useRouter();
  const invitedCode = useCaptureReferral();

  const { isAvailable: usernameAvailable, isChecking: usernameChecking, error: usernameHookError } =
    useUsernameCheck(username);

  // Read on mount rather than useSearchParams(), which suspends during
  // streaming SSR (see CLAUDE.md) — same pattern as useCaptureReferral.ts.
  // A `/sell` visitor lands here with ?intent=seller; default the checkbox
  // so seller intent isn't lost if they never scroll down to notice it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("intent") === "seller") {
      setWantsSeller(true);
    }
    // Carry a `redirect` through to sign-in. Signup finishes over email, so we
    // can't land the user on the destination ourselves — but we can make sure
    // the sign-in step they arrive at afterwards still knows where they were
    // going. Same open-redirect guard as SignIn: same-origin paths only.
    setRedirectTo(safeRedirectPath(params.get("redirect")));
  }, []);

  const clearField = (key: "email" | "password" | "username") => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validateClient = () => {
    const next: typeof fieldErrors = {};
    const trimmedUser = username.toLowerCase().trim();
    if (trimmedUser.length < 3) next.username = "Use at least 3 characters.";
    if (!email.trim()) next.email = "Enter your email.";
    else if (!EMAIL_RE.test(email.trim())) next.email = "Enter a valid email address.";
    if (!evaluatePasswordPolicy(password).ok) {
      next.password = "Meet all password requirements below.";
    }
    if (trimmedUser.length >= 3 && usernameAvailable === false) {
      next.username = usernameHookError || "That username is taken.";
    }
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!validateClient()) return;

    const trimmedUsername = username.toLowerCase().trim();

    if (usernameChecking || usernameAvailable === false) {
      setFieldErrors({ username: usernameHookError || "Check username availability." });
      return;
    }

    if (isTurnstileEnabled() && !captchaToken) {
      setFormError("Please complete the verification challenge.");
      return;
    }

    if (!termsAccepted) {
      setFormError("Please accept the Terms of Service and Privacy Policy to continue.");
      return;
    }

    setLoading(true);
    trackAuthSignupStarted("email");

    const { error: signUpError, userId } = await signUp(
      email.trim(),
      password,
      trimmedUsername,
      trimmedUsername,
      captchaToken ?? undefined,
      { termsAccepted, marketingOptIn },
    );

    if (signUpError) {
      // Turnstile tokens are single-use — force a fresh challenge for retry.
      setCaptchaToken(null);
      setTurnstileKey((k) => k + 1);
      const userMessage = signUpError.message.includes("already registered")
        ? "An account with this email already exists. Try signing in instead."
        : toUserFriendlyAuthError(signUpError.message);

      setFormError(userMessage);
      reportErrorToDiscord({
        source: "SignUp.submit",
        userMessage,
        error: signUpError,
        metadata: {
          email_domain: email.split("@")[1] || "unknown",
          raw_error: signUpError.message,
        },
      });

      setLoading(false);
      return;
    }

    if (userId) {
      trackAuthSignupCompleted({ method: "email", userId, inviteCode: invitedCode ?? undefined });
    }
    // The confirmation link signs the user in on a fresh page load, so where
    // they were headed has to survive in a pending intent — the resumer routes
    // them there on SIGNED_IN. "Apply to sell" wins over a plain redirect.
    const returnTo = wantsSeller ? "/seller/onboarding" : redirectTo;
    if (returnTo && !peekPendingIntent()) {
      savePendingIntent({ kind: "other", returnTo });
    }
    trackSignUp("email");
    setSuccess(true);
    setLoading(false);
  };

  if (success) {
    return (
      <AuthLayout heading="Check your email" subheading="We sent a confirmation link">
        <AuthFormPanel className="animate-fade-up">
          <CheckYourEmailScreen
            email={email}
            surface="signup_page"
            resendSlot={<ResendVerification email={email} />}
            footer={
              <div className="space-y-5">
                <Button
                  variant="outline"
                  className="w-full h-11"
                  onClick={() =>
                    router.push(
                      redirectTo
                        ? `/signin?redirect=${encodeURIComponent(redirectTo)}`
                        : "/signin",
                    )
                  }
                >
                  Back to sign in
                </Button>

                {/* Discord funnel — most creators connect via Discord, so prompt
                    join here while the new account is fresh + engaged. */}
                <div onClick={() => track({ event: "signup_discord_cta_clicked" })}>
                  <DiscordBanner
                    heading="Meet other UEFN creators"
                    description="Hop into the Discord while you wait for the email — share work, get help, find collaborators."
                  />
                </div>
              </div>
            }
          />
        </AuthFormPanel>
      </AuthLayout>
    );
  }

  return (
    <>
      <AuthLayout heading="Create your free account" subheading="Download UEFN assets in seconds. Save a wishlist, build a library, and sell your own.">
        <AuthSkipLink href="#signup-email-form">Skip to sign-up form</AuthSkipLink>
        <AuthFormPanel className="animate-fade-up stagger-1 space-y-4 p-5">
          {invitedCode && (
            <div className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm text-foreground">
              <Gift className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <span>You&apos;ve been invited. Create your free account and start downloading.</span>
            </div>
          )}
          <div className="space-y-2">
            <label className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-3 cursor-pointer transition-colors hover:bg-muted/50">
              <Checkbox
                checked={termsAccepted}
                onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                className="mt-0.5"
                aria-label="Agree to the Terms of Service and Privacy Policy"
              />
              <span className="text-sm leading-relaxed text-muted-foreground">
                I agree to the{" "}
                <Link href="/terms" target="_blank" className="text-primary hover:underline">Terms of Service</Link>{" "}
                and{" "}
                <Link href="/privacy" target="_blank" className="text-primary hover:underline">Privacy Policy</Link>.
              </span>
            </label>
            <label className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-3 cursor-pointer transition-colors hover:bg-muted/50">
              <Checkbox
                checked={marketingOptIn}
                onCheckedChange={(checked) => setMarketingOptIn(checked === true)}
                className="mt-0.5"
                aria-label="Receive product updates and promotions by email"
              />
              <span className="text-sm leading-relaxed text-muted-foreground">
                Email me product updates, promotions &amp; news. Optional — unsubscribe anytime.
              </span>
            </label>
          </div>

          <SocialAuthButtons
            mode="signup"
            disabled={!termsAccepted}
            onBeforeAuth={() => stashPendingConsent({ terms: termsAccepted, marketing: marketingOptIn })}
          />

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-[10px] uppercase">
              <span className="bg-card/80 px-3 text-muted-foreground font-mono-accent tracking-[0.2em]">or</span>
            </div>
          </div>

          <form
            id="signup-email-form"
            onSubmit={(e) => void handleSubmit(e)}
            aria-label="Create account with email"
            className="space-y-3"
          >
            {formError && (
              <p className="text-sm text-destructive" role="alert">
                {formError}
              </p>
            )}

            <AuthFieldShell>
              <Label htmlFor="username">Username</Label>
              <div className="relative">
                <AtSign className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <Input
                  id="username"
                  type="text"
                  placeholder="your-username"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""));
                    clearField("username");
                  }}
                  required
                  minLength={3}
                  maxLength={30}
                  autoComplete="username"
                  aria-invalid={!!fieldErrors.username}
                  aria-describedby="username-message"
                  className={cn(
                    "pl-10 pr-10 bg-secondary border-0",
                    fieldErrors.username && "border border-destructive/80 focus-visible:ring-destructive/30",
                  )}
                />
                {username.length >= 3 && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {usernameChecking ? (
                      <Skeleton className="h-4 w-4 rounded-full" aria-hidden />
                    ) : usernameAvailable === true ? (
                      <span aria-label="Username available">
                        <Check className="h-4 w-4 text-success" aria-hidden="true" />
                      </span>
                    ) : usernameAvailable === false ? (
                      <span aria-label="Username taken">
                        <X className="h-4 w-4 text-destructive" aria-hidden="true" />
                      </span>
                    ) : null}
                  </div>
                )}
              </div>
              {fieldErrors.username ? (
                <p id="username-message" className="text-xs text-destructive">{fieldErrors.username}</p>
              ) : usernameHookError && username.length > 0 ? (
                <p id="username-message" className="text-xs text-destructive">{usernameHookError}</p>
              ) : !username ? (
                <p id="username-message" className="text-xs text-muted-foreground">3–30 characters, lowercase letters, numbers, and hyphens</p>
              ) : null}
            </AuthFieldShell>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <AuthFieldShell>
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      clearField("email");
                    }}
                    required
                    autoComplete="email"
                    aria-invalid={!!fieldErrors.email}
                    aria-describedby={fieldErrors.email ? "email-error" : undefined}
                    className={cn(
                      "pl-10 bg-secondary border-0",
                      fieldErrors.email && "border border-destructive/80 focus-visible:ring-destructive/30",
                    )}
                  />
                </div>
                {fieldErrors.email && <p id="email-error" className="text-xs text-destructive">{fieldErrors.email}</p>}
              </AuthFieldShell>

              <AuthFieldShell>
                <Label htmlFor="password">Password</Label>
                <PasswordInput
                  id="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    clearField("password");
                  }}
                  required
                  autoComplete="new-password"
                  aria-invalid={!!fieldErrors.password}
                  aria-describedby={fieldErrors.password ? "password-error" : undefined}
                  className={cn(fieldErrors.password && "border border-destructive/80 focus-visible:ring-destructive/30")}
                />
                {fieldErrors.password && <p id="password-error" className="text-xs text-destructive">{fieldErrors.password}</p>}
              </AuthFieldShell>
            </div>

            <PasswordStrength password={password} />

            <label className="flex items-start gap-3 p-3 rounded-lg border border-border bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors">
              <Checkbox
                checked={wantsSeller}
                onCheckedChange={(checked) => setWantsSeller(checked === true)}
                className="mt-0.5"
              />
              <div className="space-y-0.5">
                <span className="text-sm font-medium">Apply to sell assets</span>
                <p className="text-xs text-muted-foreground">
                  After you verify your email, we&apos;ll walk you through seller onboarding, where you keep up to {HIGHEST_SELLER_SHARE_PERCENT}% of
                  every sale. Reviews typically take 1–2 business days.
                </p>
              </div>
            </label>

            <TurnstileWidget key={turnstileKey} onToken={setCaptchaToken} className="flex justify-center" />

            <FormAnticipationBar show={loading} label="Creating account" />

            <Button
              type="submit"
              className="w-full h-11 text-[15px] font-semibold"
              disabled={
                loading ||
                !termsAccepted ||
                usernameAvailable === false ||
                usernameChecking ||
                username.trim().length < 3 ||
                !evaluatePasswordPolicy(password).ok ||
                (isTurnstileEnabled() && !captchaToken)
              }
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Create account
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>

          </form>
        </AuthFormPanel>

        <div className="animate-fade-up stagger-2 text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link href="/signin" className="font-semibold text-primary hover:underline">
            Sign in
          </Link>
        </div>
      </AuthLayout>
    </>
  );
}
